"use client";

import { cva, type VariantProps } from "../../lib/cva";
import * as React from "react";
import { Box } from "../../components/ui/box";
import type { FillOption } from "../../components/ui/box-classes";
import { Button, buttonVariants, type ButtonSize } from "../../components/ui/button";
import { breadcrumbCrumbClass } from "../../components/ui/breadcrumb";
import { Popover, PopoverPopup } from "../../components/ui/popover";
import { PREVIEW_SIDE_OFFSET } from "../../components/ui/preview-card";
import { Text } from "../../components/ui/text";
import { useIsTouchDevice } from "../../hooks/use-is-touch-device";
import type { Color } from "../../lib/colors";
import { cn } from "../../lib/utils";
import type { Omittable } from "../../lib/props";

/**
 * Headless interaction shell for entity tags/chips: trigger + hover-intent
 * timers + lazy popover. `as="text"` renders an inline `Text` so a label-only
 * tag can sit inside a sentence. Until the first pointer-enter only the bare
 * trigger renders (no popover machinery) so virtualized tables of chips cost
 * nothing. Entity-agnostic by contract: kinds, labels, and data live in the
 * consumer's wrappers.
 */

const DEFAULT_OPEN_DELAY = 800;
const DEFAULT_CLOSE_DELAY = 200;

// Matches the Button `ghost` idiom so clickable tags read as clickable.
const GHOST_HOVER_BG =
  "transition-colors hover:bg-foreground/5 active:bg-foreground/5 dark:hover:bg-foreground/10 dark:active:bg-foreground/10";

// Layout-neutral: `p-1` bleeds the wash past the content, `-m-1` cancels it in
// layout so the footprint stays exactly the content and nothing shifts.
const GHOST_BOX_CLASSES = cn("rounded-sm", GHOST_HOVER_BG, "p-1 -m-1");

// Inline variant: vertical padding paints the bg without growing the line box,
// so only horizontal padding needs cancelling; clone repaints on wrapped lines.
const GHOST_TEXT_CLASSES = cn(
  "rounded-sm",
  GHOST_HOVER_BG,
  "px-1 py-0.5 -mx-1 [box-decoration-break:clone]",
);

export const entityChipVariants = cva("", {
  variants: {
    variant: {
      plain: "",
      chip: "rounded-sm",
    },
    // Caller gates `true` on `!!onClick && ghost` so a non-interactive chip never looks clickable.
    ghost: {
      true: GHOST_BOX_CLASSES,
      false: "",
    },
  },
  defaultVariants: {
    variant: "plain",
  },
});

type EntityChipShellVariantProps = VariantProps<typeof entityChipVariants>;

interface HoverIntentHandlers {
  onPointerEnter: () => void;
  onPointerLeave: () => void;
  /** Gated on a real move so a chip sliding under a stationary cursor never
   *  auto-opens; a move with a button held is a drag, never hover intent. */
  onPointerMove: (e?: { buttons?: number }) => void;
}

interface HoverIntentState {
  open: boolean;
  /** Flips true on first pointer-enter and stays true (lazy-mount gate). */
  hasHovered: boolean;
  setOpen: (open: boolean) => void;
  triggerHandlers: HoverIntentHandlers;
  contentHandlers: HoverIntentHandlers;
  /** Pin open (a descendant hover popover took the pointer). */
  hold: () => void;
  release: () => void;
}

/**
 * Lets a nested hover popover keep its ancestors open: a child holds() its
 * ancestor while its own popover is open. Without it, moving into a popover the
 * card spawned (portaled out of its DOM) fires the card's leave and unmounts it.
 */
const HoverIntentHoldContext = React.createContext<{
  hold: () => void;
  release: () => void;
} | null>(null);

/**
 * True for everything rendered inside a chip's preview popup. A preview is
 * ONE layer deep: a chip inside a card never arms a card of its own (hover or
 * click), so a stack of members inside a team card, each naming that team,
 * cannot chain popups across the screen. Its click is the whole interaction —
 * the consumer decides what the click opens (`useInsidePreviewPopup`).
 */
const InsidePreviewPopupContext = React.createContext(false);

export function useInsidePreviewPopup(): boolean {
  return React.useContext(InsidePreviewPopupContext);
}

/**
 * Every open preview's close function, in opening order. Chips are otherwise
 * fully independent — each closes only through its own grace timer — so
 * without this, a pointer sweeping a column of chips strands one preview open
 * per chip. Opening a preview closes every one already open.
 */
const openPreviews: (() => void)[] = [];

function claimPreviewSlot(close: () => void): void {
  // `close` itself is never in the stack here: a claim only runs on the
  // not-showing → showing transition, and the effect's cleanup released any
  // earlier claim before this one runs.
  for (const other of openPreviews.splice(0)) other();
  openPreviews.push(close);
}

function releasePreviewSlot(close: () => void): void {
  const index = openPreviews.indexOf(close);
  if (index !== -1) openPreviews.splice(index, 1);
}

/**
 * Synthetic events cross a portal along the REACT tree, not the DOM tree: an
 * unstopped click inside the (portaled) popup bubbles into whatever wraps the
 * chip — e.g. a selector's popover trigger, which then opens its picker behind
 * whatever the popup's row just opened. Native listeners outside the portal
 * never see popup events, so stopping the synthetic ones removes only the
 * leak. Base UI merges these with its own popup handlers, which still run.
 */
function stopSyntheticPropagation(e: React.SyntheticEvent): void {
  e.stopPropagation();
}

const POPUP_EVENT_SEAL = {
  onClick: stopSyntheticPropagation,
  onMouseDown: stopSyntheticPropagation,
  onPointerDown: stopSyntheticPropagation,
  onKeyDown: stopSyntheticPropagation,
  onKeyUp: stopSyntheticPropagation,
} as const;

/**
 * Hover-intent timers for lazy hover popovers (open after `openDelay`, close
 * after the `closeDelay` grace). Timers clear on unmount so virtualized rows
 * never leak.
 *
 * `pointerleave` is NOT reliable: browsers defer boundary events during
 * scroll, and a drag carries the chip along WITH the pointer — so an armed
 * timer (or an open popover) can outlive the hover it belongs to and surface
 * a phantom preview far from the cursor. Three guards close those holes:
 * the open timer re-checks live `:hover` state when it fires, a move with a
 * button held never arms, and document-level scroll/pointerdown listeners
 * (alive ONLY while armed or open) cancel the timer and dismiss the popover
 * when the page moves underneath it.
 */
export function useHoverIntent(
  openDelay: number = DEFAULT_OPEN_DELAY,
  closeDelay: number = DEFAULT_CLOSE_DELAY,
  /** When false every handler is a no-op and no timer arms — for chips that
   *  can't hover, so non-hover chips in a list run zero machinery. */
  enabled: boolean,
  /** The trigger's DOM node — the open timer re-checks its live `:hover`
   *  state when it fires, and the scroll guard uses it to tell page scrolls
   *  (which move the chip) from scrolls inside a floating layer. */
  triggerRef?: React.RefObject<HTMLElement | null>,
): HoverIntentState {
  const [open, setOpenState] = React.useState(false);
  const [hasHovered, setHasHovered] = React.useState(false);
  // clearTimeout on undefined is a no-op, so clears never need a guard.
  const openTimer = React.useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  const closeTimer = React.useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  // pointermove only arms the open timer while inside — guards a stray move racing in after leave.
  const insideRef = React.useRef(false);
  // Descendant popovers holding this one open; while > 0, a leave never schedules a close.
  const heldRef = React.useRef(0);
  // Mirrors `open` for the document-level guards (state is stale inside them).
  const openRef = React.useRef(false);
  const guardsRef = React.useRef<{
    onScroll: (e: Event) => void;
    onPointerDown: () => void;
  } | null>(null);

  const applyOpen = React.useCallback((next: boolean) => {
    openRef.current = next;
    setOpenState(next);
  }, []);

  const clearTimers = React.useCallback(() => {
    clearTimeout(openTimer.current);
    clearTimeout(closeTimer.current);
    openTimer.current = undefined;
    closeTimer.current = undefined;
  }, []);

  const detachGuards = React.useCallback(() => {
    if (!guardsRef.current) return;
    document.removeEventListener("scroll", guardsRef.current.onScroll, true);
    document.removeEventListener(
      "pointerdown",
      guardsRef.current.onPointerDown,
      true,
    );
    guardsRef.current = null;
  }, []);

  /** Attach/detach the document guards to match "armed or open". */
  const syncGuards = React.useCallback(() => {
    if (!openTimer.current && !openRef.current) {
      detachGuards();
      return;
    }
    if (guardsRef.current || typeof document === "undefined") return;
    const cancelArmed = () => {
      clearTimeout(openTimer.current);
      openTimer.current = undefined;
    };
    const onScroll = (e: Event) => {
      // Only a scroll that moves the chip matters. A scroll inside a floating
      // layer (the preview's own list, a nested picker — all portaled to the
      // body) never contains the trigger, so it is content interaction, not
      // the page moving under the anchor.
      const trigger = triggerRef?.current;
      const movesTrigger =
        !trigger ||
        e.target === document ||
        (e.target instanceof Element && e.target.contains(trigger));
      if (!movesTrigger) return;
      cancelArmed();
      clearTimeout(closeTimer.current);
      closeTimer.current = undefined;
      if (openRef.current) applyOpen(false);
      detachGuards();
    };
    const onPointerDown = () => {
      // A press must never ripen into a hover preview (press-and-drag holds
      // the chip under the pointer forever). Open popovers are left to
      // Base UI's own outside-press close.
      cancelArmed();
      if (!openRef.current) detachGuards();
    };
    guardsRef.current = { onScroll, onPointerDown };
    document.addEventListener("scroll", onScroll, {
      capture: true,
      passive: true,
    });
    document.addEventListener("pointerdown", onPointerDown, true);
  }, [applyOpen, detachGuards, triggerRef]);

  const setOpen = React.useCallback(
    (next: boolean) => {
      clearTimers();
      applyOpen(next);
      syncGuards();
    },
    [clearTimers, applyOpen, syncGuards],
  );

  const handleEnter = React.useCallback(() => {
    if (!enabled) return;
    setHasHovered(true);
    insideRef.current = true;
    clearTimeout(closeTimer.current);
    closeTimer.current = undefined;
    // Don't arm the open timer here: pointerenter also fires when the element
    // slides under a stationary cursor (text reflow). Wait for a real move.
  }, [enabled]);

  const handleMove = React.useCallback(
    (e?: { buttons?: number }) => {
      if (!enabled) return;
      // Buttons held = drag in progress, never hover intent.
      if (e?.buttons) return;
      if (!insideRef.current || openTimer.current) return;
      openTimer.current = setTimeout(() => {
        openTimer.current = undefined;
        // The leave that should have cancelled this timer may never have
        // fired (deferred during scroll, layout shift, drag) — trust the
        // browser's live hover state over the timer.
        const node = triggerRef?.current;
        if (node && !node.matches(":hover")) {
          syncGuards();
          return;
        }
        applyOpen(true);
        syncGuards();
      }, openDelay);
      syncGuards();
    },
    [openDelay, enabled, applyOpen, syncGuards, triggerRef],
  );

  const scheduleClose = React.useCallback(() => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => {
      closeTimer.current = undefined;
      applyOpen(false);
      syncGuards();
    }, closeDelay);
  }, [closeDelay, applyOpen, syncGuards]);

  const handleLeave = React.useCallback(() => {
    insideRef.current = false;
    clearTimeout(openTimer.current);
    openTimer.current = undefined;
    syncGuards();
    // A descendant popover owns the pointer — its release() schedules our close.
    if (heldRef.current > 0) return;
    scheduleClose();
  }, [scheduleClose, syncGuards]);

  const hold = React.useCallback(() => {
    if (!enabled) return;
    heldRef.current += 1;
    clearTimeout(closeTimer.current);
    closeTimer.current = undefined;
  }, [enabled]);

  const release = React.useCallback(() => {
    heldRef.current = Math.max(0, heldRef.current - 1);
    if (heldRef.current === 0) scheduleClose();
  }, [scheduleClose]);

  // Disabling must also discard in-flight state: an armed open timer would
  // still fire (handlers only gate NEW events), and a stale `open` would
  // re-show the popover the instant the chip is re-enabled.
  React.useEffect(() => {
    if (enabled) return;
    clearTimers();
    insideRef.current = false;
    applyOpen(false);
    detachGuards();
  }, [enabled, clearTimers, applyOpen, detachGuards]);

  React.useEffect(
    () => () => {
      clearTimers();
      detachGuards();
    },
    [clearTimers, detachGuards],
  );

  const handlers = React.useMemo<HoverIntentHandlers>(
    () => ({
      onPointerEnter: handleEnter,
      onPointerLeave: handleLeave,
      onPointerMove: handleMove,
    }),
    [handleEnter, handleLeave, handleMove],
  );

  return {
    open,
    hasHovered,
    setOpen,
    triggerHandlers: handlers,
    contentHandlers: handlers,
    hold,
    release,
  };
}

/** Opens an anchored confirm popover; only Confirm fires `onClick`. */
type ClickConfirmation = Omittable<{
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
}>;

export type EntityChipShellProps = EntityChipShellVariantProps & Omittable<{
  children: React.ReactNode;
  /** Tints the label. Applies in `chip` variant and in `as="text"` mode; a
   *  plain box chip stays untinted. The chip itself never fills. */
  color?: Color;
  /** `"box"` (default) = clickable row. `"text"` = inline Text so a label-only
   *  tag can sit inside a sentence; then `variant` is ignored. */
  as?: "box" | "text";
  /** Lazy hover-card slot — invoked only while the popover is open. `close`
   *  dismisses the preview; content that navigates (a "view profile" action)
   *  calls it so the card never lingers over the destination. */
  renderPreview?: (args: { close: () => void }) => React.ReactNode;
  onClick?: (e: React.MouseEvent<HTMLElement>) => void;
  /** Click toggles the preview popover (only when `onClick` is absent). On a
   *  coarse pointer this is how every preview opens — hover never fires
   *  there, so the shell turns it on itself. Pass `true` to offer the click
   *  on fine pointers too, for a chip whose preview is its only affordance. */
  clickOpensPreview?: boolean;
  confirmOnClick?: ClickConfirmation;
  /** Suppress hover + click entirely. */
  disabled?: boolean;
  /** Suppress the preview only — the hover on a fine pointer, the tap on a
   *  coarse one. `onClick` still works. */
  disableHover?: boolean;
  openDelay?: number;
  closeDelay?: number;
  side?: "top" | "right" | "bottom" | "left";
  align?: "start" | "center" | "end";
  /** Fill available space (box mode only). */
  fill?: FillOption;
  /** Paint the ghost hover background (default true); only shows when interactive. */
  ghost?: boolean;
  /**
   * Paint the hover wash without a click handler of the chip's own — for a
   * chip whose interaction a WRAPPER owns. The wash also holds while that
   * wrapper's popup is open (`in-data-popup-open`), so the chip stays lit
   * under the menu it anchors. `true` is for a wrapper whose only gesture is
   * secondary (a right-click context menu): the cursor stays default, since a
   * plain click does nothing and a pointer cursor would promise one.
   * `"press"` is for a wrapper that owns the LEFT press (a dropdown-menu
   * trigger around the chip): the same wash, keeping the pointer cursor that
   * real press earns. Box mode only.
   */
  hoverWash?: boolean | "press";
  /** Underline the label on hover instead of the ghost wash — the link-like,
   *  zero-padding treatment for a name that sits inline in a sentence and
   *  carries a hover preview. Only shows when interactive. */
  underlineHover?: boolean;
  /**
   * Render the box-mode trigger with a Button's exact ghost geometry at this
   * size (via `buttonVariants` classes on a Box — a non-`<button>`, so it
   * still nests inside clickable rows/anchors), for where a chip must line up
   * with adjacent buttons. `crumb` is the breadcrumb rung a `BreadcrumbLink`
   * sits on. Omit for the default content-sized, layout-neutral ghost chip.
   */
  size?: ButtonSize | "crumb";
  /** Visual↔label gap. `'xs'` for the small chip sizes so the gap stays
   *  proportional to the glyph — derived by the entity wrappers from their
   *  size prop, never set per call site. Default `'sm'`. */
  visualGap?: "sm" | "xs";
}>;

function EntityChipShellComponent({
  children,
  variant = "plain",
  color,
  as = "box",
  renderPreview,
  onClick,
  clickOpensPreview,
  confirmOnClick,
  disabled,
  disableHover,
  openDelay,
  closeDelay,
  side = "top",
  align = "start",
  fill,
  ghost = true,
  hoverWash = false,
  underlineHover = false,
  size,
  visualGap = "sm",
}: EntityChipShellProps): React.ReactElement {
  // Pointer capability decides the route into the preview, and it is decided
  // HERE, once: a coarse pointer never hovers, so the hover-intent machinery
  // stays unarmed (an armed trigger would also steal the press from an
  // enclosing tooltip trigger) and the click takes its place. A chip with a
  // click of its own keeps it — the tap performs the action, and the action's
  // destination carries what the preview would have shown.
  const isTouch = useIsTouchDevice();
  // Inside a preview popup no preview arms at all — see the context.
  const insidePreviewPopup = React.useContext(InsidePreviewPopupContext);
  const previewEnabled =
    !disabled && !disableHover && !!renderPreview && !insidePreviewPopup;
  const canHover = previewEnabled && !isTouch;
  const triggerRef = React.useRef<HTMLElement | null>(null);
  const {
    open,
    hasHovered,
    setOpen,
    triggerHandlers,
    contentHandlers,
    hold,
    release,
  } = useHoverIntent(openDelay, closeDelay, canHover, triggerRef);
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [clickOpen, setClickOpen] = React.useState(false);
  const clickToPreview =
    !onClick &&
    ((!!clickOpensPreview && !disabled && !!renderPreview && !insidePreviewPopup) ||
      (isTouch && previewEnabled));
  // The chip is the popover's ANCHOR, not its trigger, so a second press on it
  // reads to Base UI as an outside press — which closes the popup on the
  // pointer half, before our own click handler runs. Without this latch that
  // handler would toggle the freshly-closed preview straight back open and the
  // chip could never be clicked shut.
  const dismissedByChipRef = React.useRef(false);

  // While showing, hold our ancestor open so moving from its card into our
  // (portaled) popover doesn't trip its close timer; cleanup releases. Inside
  // a preview the only popover a chip still opens is its click confirmation.
  const parentHold = React.useContext(HoverIntentHoldContext);
  const isShowing = open || confirmOpen || clickOpen;
  React.useEffect(() => {
    if (!parentHold || !isShowing) return;
    parentHold.hold();
    return () => parentHold.release();
  }, [parentHold, isShowing]);

  // One preview at a time, app-wide. The confirm popover is exempt: it exists
  // to answer a click just made, and a dwell elsewhere must not dismiss it.
  const previewShowing = open || clickOpen;
  const forceClose = React.useCallback(() => {
    setOpen(false);
    setClickOpen(false);
  }, [setOpen]);
  React.useEffect(() => {
    if (!previewShowing) return;
    claimPreviewSlot(forceClose);
    return () => releasePreviewSlot(forceClose);
  }, [previewShowing, forceClose]);

  const holdContext = React.useMemo(() => ({ hold, release }), [hold, release]);

  const hoverHandlers = canHover ? triggerHandlers : undefined;
  const needsConfirmation = !!confirmOnClick && !disabled && !!onClick;

  const handleClick = React.useMemo(() => {
    if (disabled || !onClick) {
      if (!clickToPreview) return undefined;
      return () => {
        setOpen(false);
        if (dismissedByChipRef.current) {
          dismissedByChipRef.current = false;
          return;
        }
        setClickOpen((prev) => !prev);
      };
    }
    if (needsConfirmation) {
      return () => {
        setOpen(false);
        setConfirmOpen(true);
      };
    }
    return (e: React.MouseEvent<HTMLElement>) => {
      setOpen(false);
      onClick(e);
    };
  }, [disabled, onClick, needsConfirmation, clickToPreview, setOpen]);

  // The trigger is deliberately NOT a native button element: chips nest
  // inside clickable rows and anchors, and a button cannot nest inside
  // another button. When interactive it carries button semantics + keyboard
  // activation instead.
  const interactiveProps = handleClick
    ? {
        onClick: handleClick,
        role: "button" as const,
        tabIndex: 0,
        onKeyDown: (e: React.KeyboardEvent<HTMLElement>) => {
          if (e.key !== "Enter" && e.key !== " ") return;
          e.preventDefault();
          handleClick(e as unknown as React.MouseEvent<HTMLElement>);
        },
      }
    : undefined;

  const ghostActive = (!!handleClick || hoverWash) && ghost;
  const underlineClass =
    !!handleClick && underlineHover
      ? "hover:underline underline-offset-2 decoration-from-font"
      : undefined;

  // No TextClassContext in the DOM world: the tint goes on the trigger element
  // and descendant text inherits `color`. A `muted` chip is already the default
  // foreground, so it stays untinted rather than restating it.
  const tinted =
    !!color && (as === "text" || (variant === "chip" && color !== "muted"));
  const tintProps = tinted ? { "data-color": color } : undefined;
  const labelTextClass = tinted ? "text-c" : undefined;

  const trigger =
    as === "text" ? (
      <Text
        ref={triggerRef as React.Ref<HTMLSpanElement>}
        data-slot="entity-chip"
        {...tintProps}
        {...interactiveProps}
        onPointerEnter={hoverHandlers?.onPointerEnter}
        onPointerLeave={hoverHandlers?.onPointerLeave}
        onPointerMove={hoverHandlers?.onPointerMove}
        className={cn(
          interactiveProps && "select-none",
          ghostActive && GHOST_TEXT_CLASSES,
          underlineClass,
          labelTextClass,
        )}
      >
        {children}
      </Text>
    ) : size ? (
      // Sized chip: borrow Button's exact ghost geometry so it lines up with
      // adjacent buttons — variant classes on a Box, never a native button
      // element. Ink `foreground`, not the ghost's muted rest: a chip is
      // content, so its label rests at full ink (the active breadcrumb crumb
      // must match the pane title beside it). `shrink min-w-0` overrides the
      // variant's `shrink-0` so the label can truncate in dense rows.
      <Box minWidth={0}
        ref={triggerRef as React.Ref<HTMLDivElement>}
        data-slot="entity-chip"
        fill={fill}
        {...tintProps}
        {...interactiveProps}
        onPointerEnter={hoverHandlers?.onPointerEnter}
        onPointerLeave={hoverHandlers?.onPointerLeave}
        onPointerMove={hoverHandlers?.onPointerMove}
        className={cn(
          size === "crumb"
            ? breadcrumbCrumbClass("foreground")
            : buttonVariants({ ink: "foreground", size, variant: "ghost" }),
          "shrink",
          // The variant is borrowed for GEOMETRY; its hover/press washes and
          // pointer cursor are interaction affordances, so an inert chip (no
          // click) sheds them — same gate `ghostActive` applies to the
          // unsized branches. `hoverWash` keeps the wash (its interaction
          // lives on a wrapper); only its `"press"` mode keeps the pointer
          // cursor, because only there does a plain click do something.
          !handleClick && "data-pressed:bg-transparent",
          !handleClick && hoverWash !== "press" && "cursor-default",
          !handleClick && !hoverWash && "hover:bg-transparent",
          !handleClick && hoverWash && "in-data-popup-open:bg-wash-hover",
          underlineClass,
          labelTextClass,
        )}
      >
        {children}
      </Box>
    ) : (
      <Box
        ref={triggerRef as React.Ref<HTMLDivElement>}
        data-slot="entity-chip"
        direction="row"
        align="center"
        gap={visualGap}
        shrink
        minWidth={0}
        fill={fill}
        {...tintProps}
        {...interactiveProps}
        onPointerEnter={hoverHandlers?.onPointerEnter}
        onPointerLeave={hoverHandlers?.onPointerLeave}
        onPointerMove={hoverHandlers?.onPointerMove}
        className={cn(
          entityChipVariants({ variant, ghost: ghostActive }),
          !handleClick &&
            hoverWash &&
            "in-data-popup-open:bg-foreground/5 dark:in-data-popup-open:bg-foreground/10",
          !handleClick && hoverWash === "press" && "cursor-pointer",
          underlineClass,
          labelTextClass,
        )}
      >
        {children}
      </Box>
    );

  // Lazy mount: no Popover parts until first hover — or until a click
  // requests confirmation or the preview (touch devices never hover, so both
  // must mount on their own).
  const popoverMounted = confirmOpen || clickOpen || (canHover && hasHovered);

  // The returned tree keeps ONE shape for the life of the chip: the popover
  // mounts as a SIBLING of the trigger behind an always-present provider.
  // Swapping the root element type instead remounts the trigger's DOM node
  // mid-hover; the browser then has no target for the pointerleave that would
  // cancel the armed open timer, and the preview opens under a pointer that
  // already left — with nothing ever closing it.
  return (
    <HoverIntentHoldContext.Provider
      // A chip that cannot hover passes its ancestor's context through, so a
      // descendant preview still holds the nearest hover-capable ancestor.
      value={canHover ? holdContext : parentHold}
    >
      {trigger}
      {popoverMounted ? (
        <Popover
          open={open || confirmOpen || clickOpen}
          // Only honor closes from Base UI; opens come from the hover timers /
          // click / confirm request, so a plain click doesn't toggle the
          // preview over the click action.
          onOpenChange={(next, details) => {
            if (!next) {
              const target = details?.event?.target;
              dismissedByChipRef.current =
                target instanceof Node &&
                !!triggerRef.current?.contains(target);
              setOpen(false);
              setConfirmOpen(false);
              setClickOpen(false);
            }
          }}
        >
          {/* Anchor, not Trigger: a Trigger renders a button that swallows
              clicks and is invalid when the chip sits inside another clickable
              element — the trigger ref anchors the positioner instead. */}
          <PopoverPopup
            side={side}
            align={align}
            anchor={triggerRef}
            // A hover preview, so it stands the preview gap off the chip,
            // not a pressed popover's.
            sideOffset={PREVIEW_SIDE_OFFSET}
            padding="none"
            // A preview is a hover disclosure: focus stays on the page. Moving
            // it into the card lit the first action (a Send email button)
            // and opened that action's tooltip on every open. The click
            // confirmation keeps the popover's default — it answers a press
            // and its buttons are the next thing to operate.
            initialFocus={confirmOpen ? undefined : false}
            {...POPUP_EVENT_SEAL}
            onPointerEnter={contentHandlers.onPointerEnter}
            onPointerLeave={contentHandlers.onPointerLeave}
          >
            <InsidePreviewPopupContext.Provider value={true}>
              {confirmOpen && confirmOnClick ? (
                <Box padding="sm" gap="sm" maxWidth={280}>
                  <Text size="sm">{confirmOnClick.message}</Text>
                  <Box direction="row" justify="end" gap="sm">
                    <Button
                      variant="ghost"
                      size="xs"
                      onClick={() => setConfirmOpen(false)}
                    >
                      {confirmOnClick.cancelLabel ?? "Cancel"}
                    </Button>
                    <Button
                      size="xs"
                      onClick={(e) => {
                        setConfirmOpen(false);
                        onClick?.(e);
                      }}
                    >
                      {confirmOnClick.confirmLabel ?? "Continue"}
                    </Button>
                  </Box>
                </Box>
              ) : open || clickOpen ? (
                renderPreview?.({ close: forceClose })
              ) : null}
            </InsidePreviewPopupContext.Provider>
          </PopoverPopup>
        </Popover>
      ) : null}
    </HoverIntentHoldContext.Provider>
  );
}

EntityChipShellComponent.displayName = "EntityChipShell";

// Memoized: hundreds of chips render in virtualized tables.
export const EntityChipShell = React.memo(EntityChipShellComponent);
